import { useCallback, useContext, useSyncExternalStore } from 'react';
import {
  FunnelSectionsVisibilityContext,
  type FunnelSectionsVisibilityContextValue,
} from 'src/modules/settings/contexts/funnels/sections-visibility.base';

const ERROR_MESSAGE =
  'useFunnelSectionsVisibility must be used within FunnelSectionsVisibilityProvider';

/**
 * Helper hook to access the FunnelSectionsVisibilityContext.
 * @param required - If true, throws an error when context is not available.
 * @returns The context value or undefined if not required and not available.
 */
const useSectionsVisibilityContext = (
  required: boolean
): FunnelSectionsVisibilityContextValue | undefined => {
  const ctx = useContext(FunnelSectionsVisibilityContext);
  if (!ctx && required) {
    throw new Error(ERROR_MESSAGE);
  }
  return ctx;
};

/**
 * Hook to access the funnel sections visibility context.
 * Must be used within a FunnelSectionsVisibilityProvider.
 * @returns The funnel sections visibility context value.
 * @throws Error if used outside of FunnelSectionsVisibilityProvider.
 */
export const useFunnelSectionsVisibility = () =>
  useSectionsVisibilityContext(true)!;

/**
 * Hook to subscribe to the expanded state of a specific section.
 * Only re-renders when that section changes, avoiding global re-renders.
 * @param id - The unique identifier of the section.
 * @param initiallyExpanded - The initial expanded state of the section.
 * @returns Whether the section is currently expanded.
 */
export const useSectionExpanded = (id: string, initiallyExpanded: boolean) => {
  const ctx = useFunnelSectionsVisibility();
  const subscribe = useCallback(
    (listener: () => void) => ctx.subscribe(id, listener),
    [ctx, id]
  );
  const getSnapshot = () => ctx.getExpanded(id) ?? initiallyExpanded;
  const getServerSnapshot = () => initiallyExpanded;
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
